import React from "react";
import {
  AbsoluteFill,
  interpolate,
  spring,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import { COLORS, FONTS, SPRING } from "./constants";
import { GridBackground } from "../EAKCArchitecture/components/GridBackground";

// Artifact-tier quarantine drops test / eval / screenshot chunks BEFORE ranking —
// an epistemic tier decided orthogonally to embedding similarity. ~158 primary
// chunks are kept for this query (from the real prod pipeline trace).
interface PoolItem {
  name: string;
  tier: "primary" | "test" | "eval" | "screenshot";
  keep: boolean;
}

const POOL: PoolItem[] = [
  { name: "src/lib/rag/retrieval.ts", tier: "primary", keep: true },
  { name: "src/lib/rag/__tests__/retrieval.test.ts", tier: "test", keep: false },
  { name: "src/lib/rag/reranker.ts", tier: "primary", keep: true },
  { name: "src/lib/eval/answer-golden.ts", tier: "eval", keep: false },
  { name: "src/app/api/v1/chat/completions/route.ts", tier: "primary", keep: true },
  { name: "screenshots/feature14-signup.png", tier: "screenshot", keep: false },
  { name: "src/lib/rag/graph-augmented-retrieval.ts", tier: "primary", keep: true },
];

const TIER_COLOR: Record<PoolItem["tier"], string> = {
  primary: COLORS.accentGreen,
  test: COLORS.accentYellow,
  eval: COLORS.accentOrange,
  screenshot: COLORS.accentRed,
};

export const SceneQuarantine: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const titleAppear = spring({ frame, fps, config: SPRING.snappy });
  const exitOpacity = interpolate(frame, [210, 240], [1, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  // Gate "fires" at frame 90 — dropped rows dim + strike from then on.
  const gateFired = frame > 90;
  const gateAppear = spring({ frame: frame - 55, fps, config: SPRING.bouncy });
  const isFiltering = frame > 85 && frame < 140;

  // Kept-count animates to 158 after the gate fires.
  const keptCount = Math.round(
    interpolate(
      spring({ frame: frame - 120, fps, config: SPRING.gentle }),
      [0, 1],
      [0, 158],
    ),
  );

  return (
    <AbsoluteFill style={{ opacity: exitOpacity }}>
      <GridBackground opacity={0.06} />

      {/* Title */}
      <div
        style={{
          position: "absolute",
          top: 50,
          left: "50%",
          transform: "translate(-50%, 0)",
          textAlign: "center",
          opacity: interpolate(titleAppear, [0, 1], [0, 1]),
        }}
      >
        <div style={{ fontSize: 42, fontFamily: FONTS.heading, fontWeight: 700, color: COLORS.textPrimary }}>
          Step 4:{" "}
          <span style={{ color: COLORS.accentRed }}>Artifact-Tier Quarantine</span>
        </div>
        <div style={{ fontSize: 18, fontFamily: FONTS.body, color: COLORS.textSecondary, marginTop: 8 }}>
          Drop test / eval / screenshot chunks — an epistemic tier, decided orthogonally to embedding
        </div>
      </div>

      {/* Left: fused candidate pool (mixed tiers) */}
      <div style={{ position: "absolute", left: 90, top: 210, width: 620 }}>
        <div
          style={{
            fontFamily: FONTS.mono,
            fontSize: 13,
            color: COLORS.textMuted,
            textTransform: "uppercase",
            letterSpacing: 1.5,
            marginBottom: 14,
            opacity: interpolate(spring({ frame: frame - 10, fps, config: SPRING.snappy }), [0, 1], [0, 1]),
          }}
        >
          Fused candidate pool
        </div>

        {POOL.map((item, i) => {
          const rowAppear = spring({ frame: frame - 18 - i * 8, fps, config: SPRING.snappy });
          const dropped = gateFired && !item.keep;
          const c = TIER_COLOR[item.tier];
          return (
            <div
              key={i}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: "9px 16px",
                marginBottom: 8,
                borderRadius: 10,
                background: dropped ? `${COLORS.bgCard}60` : `${item.keep ? COLORS.accentGreen : c}08`,
                border: `1px solid ${dropped ? COLORS.border : `${item.keep ? COLORS.accentGreen : c}30`}`,
                opacity: interpolate(rowAppear, [0, 1], [0, dropped ? 0.35 : 1]),
                transform: `translateX(${interpolate(rowAppear, [0, 1], [-20, 0])}px)`,
              }}
            >
              <span
                style={{
                  fontFamily: FONTS.mono,
                  fontSize: 13,
                  color: dropped ? COLORS.textMuted : COLORS.textSecondary,
                  flex: 1,
                  textDecoration: dropped ? "line-through" : "none",
                }}
              >
                {item.name}
              </span>
              <span
                style={{
                  fontFamily: FONTS.mono,
                  fontSize: 10,
                  fontWeight: 700,
                  textTransform: "uppercase",
                  color: c,
                  padding: "2px 8px",
                  borderRadius: 4,
                  background: `${c}18`,
                }}
              >
                {item.tier}
              </span>
              <span style={{ fontSize: 14, minWidth: 20, textAlign: "center" }}>
                {gateFired ? (item.keep ? "✅" : "🗑️") : "…"}
              </span>
            </div>
          );
        })}
      </div>

      {/* Center: quarantine gate */}
      <div
        style={{
          position: "absolute",
          left: "50%",
          top: 470,
          transform: `translate(-50%, -50%) scale(${interpolate(gateAppear, [0, 1], [0.3, 1])})`,
          opacity: interpolate(gateAppear, [0, 1], [0, 1]),
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 10,
        }}
      >
        <div
          style={{
            width: 104,
            height: 104,
            borderRadius: 24,
            background: `linear-gradient(135deg, ${COLORS.accentRed}25, ${COLORS.accentOrange}12)`,
            border: `2px solid ${COLORS.accentRed}50`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 44,
            boxShadow: isFiltering ? `0 0 34px ${COLORS.accentRed}30` : "none",
          }}
        >
          🧹
        </div>
        <span style={{ fontFamily: FONTS.mono, fontSize: 13, color: isFiltering ? COLORS.accentRed : COLORS.textMuted }}>
          {isFiltering ? "quarantining…" : "epistemic tier"}
        </span>
        <span style={{ fontFamily: FONTS.mono, fontSize: 11, color: COLORS.textMuted, maxWidth: 180, textAlign: "center" }}>
          not similarity · no re-embed
        </span>
      </div>

      {/* Right: kept primary + count */}
      <div
        style={{
          position: "absolute",
          right: 110,
          top: 250,
          width: 420,
          textAlign: "center",
          opacity: interpolate(spring({ frame: frame - 115, fps, config: SPRING.snappy }), [0, 1], [0, 1]),
        }}
      >
        <div
          style={{
            fontFamily: FONTS.mono,
            fontSize: 13,
            color: COLORS.textMuted,
            textTransform: "uppercase",
            letterSpacing: 1.5,
            marginBottom: 8,
          }}
        >
          Kept · primary chunks
        </div>
        <div
          style={{
            fontFamily: FONTS.mono,
            fontSize: 120,
            fontWeight: 800,
            lineHeight: 1,
            color: COLORS.accentGreen,
            textShadow: `0 0 40px ${COLORS.accentGreen}40`,
          }}
        >
          {keptCount}
        </div>
        <div
          style={{
            marginTop: 16,
            padding: "12px 18px",
            borderRadius: 12,
            background: `${COLORS.accentRed}0D`,
            border: `1px solid ${COLORS.accentRed}30`,
            fontFamily: FONTS.mono,
            fontSize: 14,
            color: COLORS.textSecondary,
            lineHeight: 1.6,
          }}
        >
          test · eval · screenshot chunks
          <br />
          <span style={{ color: COLORS.accentRed, fontWeight: 700 }}>dropped before ranking</span>
        </div>
      </div>
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
